import { map } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { partitionMap } from 'fp-ts/Array';
import { left, right } from 'fp-ts/Either';
import { AppClockService, DateTime, FeatureFlagService, Interval, IntervalType } from '@mews/framework';
import { ApplicationState } from '@mews-commander/types';
import { SimpleReservationData, SimpleResourceUseResult } from '@mews-commander/data-access';
import { ResourceUseResource } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { getCurrentViewportRange, getFlatTimelineResourceUses } from '../reducers';
import { getSelectedTimeUnit } from '../selectors/view';
import { TimelineBlock } from '../types';
import { transformSimpleResourceUsesPayload } from './resourceUse';
import { intervalToUTC } from './timeUnit';

const OPTIMIZE_MIN_VALUE = 0;
const OPTIMIZE_MAX_VALUE = 14;

export const getOptimizeRangeUTC = (getState: () => ApplicationState, origin: DateTime) => {
    const state = getState();
    const timeUnit = getSelectedTimeUnit(state);
    const { startX, endX } = getCurrentViewportRange(state);
    const diff = endX - startX;
    const low = startX < OPTIMIZE_MIN_VALUE ? OPTIMIZE_MIN_VALUE : startX;
    const high = diff > OPTIMIZE_MAX_VALUE ? Number(startX) + OPTIMIZE_MAX_VALUE : endX;
    const newInterval = Interval.create({ low, high });

    return intervalToUTC(newInterval, timeUnit, origin);
};

export const getAssignments = (
    { reservationData }: SimpleResourceUseResult,
    getState: () => ApplicationState,
    appClockService: AppClockService,
    featureFlagService: FeatureFlagService
) => {
    const resourceUses = getFlatTimelineResourceUses(getState());

    const resourceUsesUpdated = map((resourceUse: SimpleReservationData) => {
        const storedResourceUse = reservationData?.find(({ id }) => id === resourceUse.id);
        return storedResourceUse
            ? { ...resourceUse, assignedResourceId: storedResourceUse.assignedResourceId }
            : resourceUse;
        // @ts-expect-error $TSFixMeError: Argument of type 'KeyByIdTyped<Identifiable>' is not assignable to parameter of type 'SimpleDataReservation[] | List<Sim...
    })(resourceUses);

    return transformSimpleResourceUsesPayload(
        { reservationData: resourceUsesUpdated },
        getState,
        appClockService,
        featureFlagService
    );
};

export const getResourceInterval = (resources: Array<ResourceUseResource>) => {
    const startResource = resources.reduce((min, resource) => (resource.minY < min.minY ? resource : min));
    const endResource = resources.reduce((max, resource) => (resource.maxY > max.maxY ? resource : max));
    return Interval.create({
        high: endResource.maxY,
        low: startResource.minY,
    });
};

const doesIntervalContainAnotherInterval = (interval1: IntervalType, interval2: IntervalType) =>
    interval1.low <= interval2.low && interval1.high >= interval2.high;

export const areCalendarEventsFetched = (loadingBlock: TimelineBlock, loadedBlocks: TimelineBlock[]) =>
    loadedBlocks.some((block) =>
        doesIntervalContainAnotherInterval(block.horizontalRange, loadingBlock.horizontalRange)
    );

export const partitionVisibleResources = (resources: ResourceUseResource[]) => {
    const separatedResults = pipe(
        resources,
        partitionMap((resource) => (resource.isSlot ? right(resource.id) : left(resource.id)))
    );
    return [separatedResults.left, separatedResults.right];
};

export const getMissingBlocks = (loadingBlock: TimelineBlock, processedBlocks: Array<TimelineBlock>) => {
    if (processedBlocks.length === 0) {
        return [loadingBlock];
    }
    const missingBlocks = processedBlocks.reduce((newBlocks: TimelineBlock[], block, index) => {
        const newLoadingBlocks = index === 0 ? [loadingBlock] : newBlocks;
        const missingBlocksForCurrentBlock = newLoadingBlocks.flatMap((newLoadingBlock) =>
            getMissingBlocksForOneBlock(newLoadingBlock, block)
        );
        return missingBlocksForCurrentBlock;
    }, []);
    return missingBlocks;
};

const getMissingBlocksForOneBlock = (loadingBlock: TimelineBlock, block: TimelineBlock) => {
    const newBlocks: TimelineBlock[] = [];
    const missingHorizontalRanges = Interval.difference(loadingBlock.horizontalRange, block.horizontalRange);
    const missingVerticalRanges = Interval.difference(loadingBlock.verticalRange, block.verticalRange);
    if (doesIntervalContainAnotherInterval(block.horizontalRange, loadingBlock.horizontalRange)) {
        if (!doesIntervalContainAnotherInterval(block.verticalRange, loadingBlock.verticalRange)) {
            const blocks = missingVerticalRanges.map((range) => ({
                verticalRange: range,
                horizontalRange: loadingBlock.horizontalRange,
            }));
            newBlocks.push(...blocks);
        }
    } else if (Interval.collides(loadingBlock.horizontalRange, block.horizontalRange)) {
        if (doesIntervalContainAnotherInterval(block.verticalRange, loadingBlock.verticalRange)) {
            const blocks = missingHorizontalRanges
                .filter((range) => range.low !== range.high)
                .map((range) => ({
                    verticalRange: loadingBlock.verticalRange,
                    horizontalRange: range,
                }));
            newBlocks.push(...blocks);
        } else {
            const intersectingHorizontalRange = Interval.intersection(
                loadingBlock.horizontalRange,
                block.horizontalRange
            );
            const intersectingVerticalRange = Interval.intersection(loadingBlock.verticalRange, block.verticalRange);
            if (
                intersectingHorizontalRange.high === intersectingHorizontalRange.low ||
                intersectingVerticalRange.high === intersectingVerticalRange.low
            ) {
                newBlocks.push(loadingBlock);
            } else {
                const blocks1 = missingHorizontalRanges.map((range) => ({
                    verticalRange: loadingBlock.verticalRange,
                    horizontalRange: range,
                }));
                const block2 = missingVerticalRanges.map((range) => ({
                    verticalRange: range,
                    horizontalRange: intersectingHorizontalRange,
                }));
                newBlocks.push(...blocks1.concat(block2));
            }
        }
    } else {
        newBlocks.push(loadingBlock);
    }

    return newBlocks;
};
